import { Middleware, AnyAction } from "redux";

interface ActionWithPayload extends AnyAction {
    payload?: {
        name?: string;
        message?: string;
        response?: unknown;
        status?: unknown;
        statusText?: unknown;
        stack?: string;
        error?: {
            name?: string;
            message?: string;
            response?: unknown;
            status?: unknown;
            statusText?: unknown;
            stack?: string;
        };
    };
}

type SerializableError = Error & {
    response?: unknown;
    status?: unknown;
    statusText?: unknown;
};

/**
 * Middleware to serialize non-serializable objects in RSAA error actions
 * This should be placed after the apiMiddleware in the middleware chain
 */
export const rsaaErrorSerializerMiddleware: Middleware =
    () => (next) => (action: ActionWithPayload) => {
        // Check if this is an error action from RSAA (typically ends with _FAILURE or _ERROR)
        if (
            action.type &&
            (action.type.endsWith("_FAILURE") || action.type.endsWith("_ERROR"))
        ) {
            const serializedAction = { ...action };

            // Serialize the payload if it contains non-serializable objects
            if (action.payload) {
                if (action.payload.name === "ApiError") {
                    serializedAction.payload = {
                        name: action.payload.name,
                        message: action.payload.message,
                        response: action.payload.response,
                        status: action.payload.status,
                        statusText: action.payload.statusText,
                        stack: action.payload.stack,
                    };
                } else if (action.payload.error instanceof Error) {
                    const error = action.payload.error as SerializableError;
                    serializedAction.payload = {
                        ...action.payload,
                        error: {
                            name: error.name,
                            message: error.message,
                            response: error.response,
                            status: error.status,
                            statusText: error.statusText,
                            stack: error.stack,
                        },
                    };
                } else if (
                    action.payload.response &&
                    typeof action.payload.response === "object" &&
                    action.payload.response.constructor !== Object
                ) {
                    // Handle Response objects or other non-plain objects
                    serializedAction.payload = {
                        ...action.payload,
                        response: action.payload.response,
                    };
                }
            }
            return next(serializedAction);
        }

        return next(action);
    };
